/**
 * Basic random number generator using Mulberry32 algorithm.
 *
 * Do not use for cryptographic purposes.
 */
const mulberry32 = (seed: number) => {
	let calcSeed = seed;
	return () => {
		calcSeed |= 0;
		calcSeed = (calcSeed + 0x6d2b79f5) | 0;
		let t = Math.imul(calcSeed ^ (calcSeed >>> 15), 1 | calcSeed);
		t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
		return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
	};
};

/**
 * Simple random number generator that returns same random value if given same seed.
 */
export const random = (seed?: number): number => {
	if (typeof seed === 'undefined') {
		return Math.random();
	}
	return mulberry32(seed)();
};

/**
 * Get random number between min and max.
 *
 * Provide array of min and max as range within which random number is generated.
 *
 * Provide seed to get predictable random number.
 */
export const randomWithin = ([min, max] = [0, 1], seed?: number) => {
	return random(seed) * (max - min) + min;
};

/**
 * Get random number inside a set of ranges.
 *
 * Provide array of arrays with min and max as range within which random number is generated.
 *
 * Provide seed to get predictable random number.
 *
 * Example:
 * ```
 * const randomNumber = randomWithinRanges([[0,1],[10,20]], seed);
 * ```
 * This returns random number that is either between 0 and 1 or 10 and 20.
 */
export const randomWithinRanges = (ranges = [[0, 1]], seed?: number) => {
	const [min, max] = randomFromList(ranges, seed);
	return randomWithin([min, max], seed);
};

/**
 * Get random number that is not equal to given number.
 *
 * Provide minDistance to get number that is at least minDistance away from given number.
 *
 * Provide array of min and max as range within which random number is generated.
 *
 * Provide seed to get predictable random number.
 *
 * @return  {[type]}  [return description]
 */
export const randomThatIsNot = (numberToAvoid: number, minDistance: number, [min, max] = [0, 1], seed?: number) => {
	let result = randomWithin([min, max], seed);
	while (Math.abs(result - numberToAvoid) < minDistance) {
		result = randomWithin([min, max], seed);
	}
	return result;
};

/**
 * Return random item from list
 *
 * Provide seed to get predictable random item.
 */
export const randomFromList = <T>(listOfItems: T[], seed?: number) => {
	const randomIndex = random(seed) * listOfItems.length;
	return listOfItems[Math.floor(randomIndex)];
};
